import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Button } from "../Button/Button";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useLogout } from "../../Hooks/useLogout";
import { existsUser } from "../../Services/usuario_service";
import { getInfoRenap } from "../../Services/renap_service";
import { RegisterUser, AuthUser } from "../../Services/login_service";
import { getPermisoRol } from "../../Services/permisos_service";
import { setAuthUser } from "../../Slices/Login/loginSlice";
import { setPermisosAsignados } from "../../Slices/Permisos/permisosSlice";
import { storageService } from "../../Services/storage_service";
import { useTokenExpiration } from "../../Hooks/useTokenExpiration";
import "./CapacitacionPersonalizada.scss";

export const CapacitacionPersonalizada = ({ open, onClose, decryptedDpi }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout } = useLogout();
  const { authUser: user } = useSelector((state) => state.auth);
  const { startTokenTimer } = useTokenExpiration();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const removeDpiFromUrl = () => {
    if (searchParams.has("dpi")) {
      searchParams.delete("dpi");
      setSearchParams(searchParams);
    }
  };

  const handleContinuar = async () => {
    if (!decryptedDpi) return;

    setLoading(true);
    try {
      if (user?.usuario && user.usuario !== decryptedDpi) {
        logout();
      }

      const userValidation = await existsUser({ user: decryptedDpi });
      const canCreateUser = Boolean(userValidation.result);

      if (!canCreateUser) {
        await handleLogin();
      } else {
        await handleRegistro();
      }
    } catch (error) {
      console.error("Error en handleContinuar:", error);
      showSnackbar("Error al procesar la solicitud", "error");
      removeDpiFromUrl();
    } finally {
      setLoading(false);
    }
  };

  const handleRegistro = async () => {
    try {
      let registrationData = {
        user: decryptedDpi,
        dpi: decryptedDpi,
        nombres: "",
        apellidos: "",
        fechaNacimiento: "",
        telefono: "",
        foto: "",
      };

      const infoRenap = await getInfoRenap({ DPI: decryptedDpi });
      const validDpi = Boolean(infoRenap.result);

      if (!validDpi) {
        const errorMessage = infoRenap?.message || "Error al validar con RENAP";
        showSnackbar(errorMessage, "error");
        removeDpiFromUrl();
        return;
      }

      const nombres = [
        infoRenap.data.PRIMER_NOMBRE,
        infoRenap.data.SEGUNDO_NOMBRE,
        infoRenap.data.TERCER_NOMBRE,
      ]
        .filter((nombre) => nombre && nombre.trim() !== "")
        .join(" ");

      const apellidos = [
        infoRenap.data.PRIMER_APELLIDO,
        infoRenap.data.SEGUNDO_APELLIDO,
      ]
        .filter((apellido) => apellido && apellido.trim() !== "")
        .join(" ");

      const fechaRenap = infoRenap.data.FECHA_NACIMIENTO;
      const [dia, mes, año] = fechaRenap.split("/");
      const fechaNacimiento = `${año}-${mes.padStart(2, "0")}-${dia.padStart(
        2,
        "0"
      )}`;

      registrationData = {
        ...registrationData,
        nombres,
        apellidos,
        fechaNacimiento,
        foto: infoRenap.data.FOTO,
      };

      const respUser = await RegisterUser(registrationData);

      if (respUser.result === 1 && respUser.data && respUser.data.length > 0) {
        dispatch(setAuthUser(respUser.data[0]));
        storageService.setItem("user", respUser.data[0]);

        showSnackbar("Cuenta creada e iniciada sesión exitosamente", "success");

        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        showSnackbar("Error al crear la cuenta", "error");
        removeDpiFromUrl();
      }
    } catch (error) {
      console.error("Error en handleRegistro:", error);
      showSnackbar("Error al crear la cuenta", "error");
      removeDpiFromUrl();
    }
  };

  const handleLogin = async () => {
    try {
      const loginData = {
        user: decryptedDpi,
        clave: decryptedDpi,
      };

      const resp = await AuthUser(loginData);

      if (Boolean(resp.result)) {
        storageService.setItem("user", resp.data[0]);
        sessionStorage.setItem("user", JSON.stringify(resp.data[0]));
        startTokenTimer(resp.data[0]);
        const permisosRes = await getPermisoRol(resp.data[0]);
        dispatch(setPermisosAsignados(permisosRes));
        dispatch(setAuthUser(resp.data[0]));

        showSnackbar("Inicio de sesión exitoso", "success");

        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        showSnackbar(resp.message, "error");
        removeDpiFromUrl();
      }
    } catch (error) {
      console.error("Error en handleLogin:", error);
      showSnackbar("Error al iniciar sesión", "error");
      removeDpiFromUrl();
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={onClose}
      className="capacitacion-personalizada-dialog"
    >
      <DialogTitle className="capacitacion-personalizada-dialog__title">
        Capacitación Personalizada
      </DialogTitle>
      <DialogContent>
        <p className="capacitacion-personalizada-dialog__description">
          Has accedido mediante un enlace personalizado. Al continuar, se creará
          tu cuenta automáticamente o se iniciará sesión si ya estás registrado.
          Podrás ingresar posteriormente usando solo tu número de DPI.
        </p>
      </DialogContent>
      <DialogActions>
        <div className="capacitacion-personalizada-dialog__actions">
          <Button
            onClick={() => {
              removeDpiFromUrl();
              onClose();
            }}
            size="medium"
            variant="secundario"
            disabled={loading}
          >
            Cerrar
          </Button>
          <Button
            onClick={handleContinuar}
            autoFocus
            size="medium"
            disabled={loading || !decryptedDpi}
          >
            {loading && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
            Continuar
          </Button>
        </div>
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
